import { getMainColors } from "constants/colors";
import { useEffect, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Placement } from "react-bootstrap/esm/types";

type OverflowLabelProps = {
    text: string;
    isVisible: boolean;
    labelClassName?: string;
    tooltipClassName?: string;
    tooltipPlacement?: Placement | undefined; 
    labelStyle?: React.CSSProperties;
    tooltipStyle?: React.CSSProperties;
    labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
}
  

export default function OverflowLabel({ text, isVisible, labelClassName, tooltipClassName, tooltipPlacement, labelStyle, tooltipStyle, labelProps }: OverflowLabelProps) {
    const labelRef = useRef(null);

    const [isTextOverflowing, setIsTextOverflowing] = useState<boolean>(false);

    function isLabelOverflowing(element: HTMLLabelElement | null) {
        if (element === null) {
            return false;
        }
        
        return element.offsetWidth < element.scrollWidth;
    }

    useEffect(() => {
        if (isVisible) {
            // The element just became visible, recalculate here
            setIsTextOverflowing(isLabelOverflowing(labelRef.current));
        }
    }, [isVisible]); // Depend on the visibility state

    useEffect(() => {
        // Function to check and set overflow state
        const checkOverflow = () => {
            setIsTextOverflowing(isLabelOverflowing(labelRef.current));
        };

        // Check overflow on mount and text change
        checkOverflow();

        // Add event listener for window resize (zoom changes)
        window.addEventListener('resize', checkOverflow);

        // Cleanup event listener on component unmount
        return () => window.removeEventListener('resize', checkOverflow);
    }, [text]);

    return (
        <OverlayTrigger
            placement={tooltipPlacement ?? "top"}
            show={isTextOverflowing ? undefined : false}
            delay={{ show: 50, hide: 50 }}
            overlay={<Tooltip className={`${tooltipClassName ?? ""}`} style={{...tooltipStyle, zIndex: 100000, pointerEvents: "none" }}>{text}</Tooltip>}
    >
        <label className={`overflow-hidden text-truncate ${labelClassName ?? ""}`}
            {...labelProps}
            ref={labelRef}
            style={{
                ...labelStyle,
        }}>{text}</label>
        </OverlayTrigger>
    );
}